import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { getUserFriendlyError } from "@/lib/utils/error-messages";
import { trackAction, logger } from "@/lib/utils/logger";
import { pharmacyLogin, resendVerification } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/auth-provider";
import { pharmacyLoginResponseSchema } from "@/lib/validation/schemas";
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from "@repo/ui";
import { Loader2, Shield, Clock, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { CapsLockIndicator } from "@/components/forms";
import { useCapsLock } from "@/hooks";

export const Route = createFileRoute("/_auth/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailVerificationError, setIsEmailVerificationError] =
    useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { isCapsLockOn, capsLockProps } = useCapsLock();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsEmailVerificationError(false);
    setResendSuccess(false);
    setIsLoading(true);

    trackAction("login_started", "ui", { email });

    try {
      const data = await pharmacyLogin({ email, password });

      trackAction("login_success", "ui");

      const parsed = pharmacyLoginResponseSchema.safeParse(data);
      if (!parsed.success) {
        logger.error("Login response validation failed", parsed.error, {
          operation: "login",
          metadata: { email },
        });
        setError("Login failed. Please try again.");
        return;
      }

      // If 2FA is required, redirect to verification page
      if (parsed.data.requires2fa) {
        sessionStorage.setItem(
          "pharmacy",
          JSON.stringify(parsed.data.pharmacy)
        );
        navigate({ to: "/2fa-verify", search: { type: "pharmacy" } });
        return;
      }

      // Refresh auth context so protected routes see the new session
      // Fire-and-forget: don't let a transient refetch failure block the login flow
      refetch().catch((err) => {
        console.warn("Auth refetch failed after successful login:", err);
      });

      // Store pharmacy info in session storage
      sessionStorage.setItem("pharmacy", JSON.stringify(parsed.data.pharmacy));

      // If pharmacy needs first user, redirect to dashboard (modal will show)
      if (parsed.data.needsFirstUser) {
        navigate({ to: "/dashboard" });
      } else {
        // Otherwise redirect to user selection
        navigate({ to: "/select-user" });
      }
    } catch (err) {
      // Log technical details for debugging
      console.error("Login error:", err);

      // Show user-friendly error message
      const errorMessage = getUserFriendlyError(
        err,
        "Login failed. Please try again."
      );
      setError(errorMessage);

      // Check if it's an email verification error
      const statusCode = (err as { statusCode?: number }).statusCode;
      if (statusCode === 403 || errorMessage.toLowerCase().includes("verify")) {
        setIsEmailVerificationError(true);
      }

      logger.error("Login failed", err, {
        operation: "login",
        metadata: { email },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError("Unable to resend verification. Please enter your email.");
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      await resendVerification({ email });
      setResendSuccess(true);
      setError("");
    } catch (err) {
      const errData = err as { error?: string };
      setError(errData.error || "Failed to resend verification email");
      console.error("Resend error:", err);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 lg:p-6 bg-gradient-to-br from-background via-background to-muted/20">
      <div className="flex flex-col lg:flex-row gap-0 max-w-5xl w-full my-4 lg:my-8 max-h-[calc(100vh-2rem)] lg:max-h-[calc(100vh-4rem)]">
        {/* Login Card */}
        <Card className="flex-1 shadow-2xl border-0 overflow-hidden lg:rounded-r-none flex flex-col max-h-full">
          <CardHeader className="pb-3 pt-5 lg:pb-4 lg:pt-6 flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img
                src="/logo-with-text-blue.png"
                alt="C2 Vault"
                className="mb-4 h-6 w-auto"
              />
              <CardTitle className="text-lg lg:text-xl font-semibold tracking-tight">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-xs lg:text-sm mt-1">
                Sign in to your pharmacy account
              </CardDescription>
            </motion.div>
          </CardHeader>
          <CardContent className="px-6 lg:px-8 pb-6 lg:pb-8 overflow-y-auto flex-1">
            <form onSubmit={handleLogin} className="space-y-3 lg:space-y-4">
              <div className="space-y-1.5">
                <Label
                  htmlFor="pharmacy-email"
                  className="text-xs lg:text-sm font-medium"
                >
                  Email Address
                </Label>
                <Input
                  id="pharmacy-email"
                  name="pharmacy-email"
                  type="email"
                  placeholder="admin@pharmacy.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className="h-9 lg:h-10 text-sm transition-all duration-200"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="pharmacy-key"
                  className="text-xs lg:text-sm font-medium"
                >
                  Password
                </Label>
                <Input
                  id="pharmacy-key"
                  name="pharmacy-key"
                  type="text"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="h-9 lg:h-10 text-sm transition-all duration-200"
                  style={{ WebkitTextSecurity: "disc" } as React.CSSProperties}
                  data-testid="login-password-input"
                  {...capsLockProps}
                />
                <CapsLockIndicator isCapsLockOn={isCapsLockOn} />
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert
                    variant="destructive"
                    className="border-destructive/50 py-2"
                    data-testid="login-error"
                  >
                    <AlertDescription className="text-xs lg:text-sm">
                      {error}
                      {isEmailVerificationError && (
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleResendVerification}
                            disabled={isResending}
                            className="w-full h-8 text-xs"
                          >
                            {isResending ? (
                              <>
                                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              "Resend Verification Email"
                            )}
                          </Button>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {resendSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert className="bg-green-500/5 border-green-500/20 py-2">
                    <AlertDescription className="text-xs lg:text-sm">
                      Verification email sent! Check your inbox.
                      {import.meta.env.DEV && (
                        <>
                          {" "}
                          Or check{" "}
                          <a
                            href="http://127.0.0.1:54324"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline font-medium"
                          >
                            Mailpit
                          </a>
                          .
                        </>
                      )}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              <Button
                type="submit"
                className="w-full h-9 lg:h-10 text-sm font-medium"
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t flex items-center justify-between text-xs lg:text-sm text-muted-foreground">
              <div>
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="font-medium text-primary hover:underline transition-colors"
                >
                  Sign up
                </Link>
              </div>
              <Link
                to="/forgot-password"
                className="hover:underline transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Panel - Attached to card side on desktop */}
        <div className="hidden lg:block lg:w-80 xl:w-96 bg-gradient-to-br from-primary via-primary to-primary/90 shadow-2xl lg:rounded-r-3xl relative overflow-hidden flex-shrink-0">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary to-primary/80" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-3xl" />

          {/* Content */}
          <div className="relative h-full flex flex-col justify-center p-6 lg:p-8">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="space-y-5 lg:space-y-6"
            >
              {/* Header */}
              <div>
                <h2 className="text-xl font-bold text-white mb-1.5">
                  Secure C2 Tracking
                </h2>
                <p className="text-white/80 text-xs">
                  Trusted by pharmacies nationwide
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3 lg:space-y-4">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="flex items-start gap-2.5"
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-xs mb-0.5">
                      DEA Compliant
                    </h3>
                    <p className="text-white/80 text-xs leading-relaxed">
                      Stay 100% compliant with federal regulations
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="flex items-start gap-2.5"
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex-shrink-0">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-xs mb-0.5">
                      Save 15+ Hours Monthly
                    </h3>
                    <p className="text-white/80 text-xs leading-relaxed">
                      Automated tracking eliminates manual logging
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="flex items-start gap-2.5"
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-xs mb-0.5">
                      Audit-Ready Reports
                    </h3>
                    <p className="text-white/80 text-xs leading-relaxed">
                      Generate reports in seconds, not hours
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Bottom highlight */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="pt-4 border-t border-white/10"
              >
                <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm">
                  <div className="w-1 h-1 rounded-full bg-white animate-pulse" />
                  <span className="text-white font-medium text-xs">
                    Secure, reliable, always compliant
                  </span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
